import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import {
  HEALTH_LABELS as CUSTOMER_HEALTH_LABELS, OBJECTIVE_STATE_LABELS, STATUS_ACTIONS, customerErrorMessage, formatDay, historyLabel, primaryAction,
} from "../../lib/customers";
import { HEALTH_LABELS, healthTone } from "../../lib/pilots";
import { HealthBadge, PhaseBadge, StatusBadge } from "./CustomerBits";
import { ActivationDialog, EditAccountDialog, HealthDialog, ObjectiveDialog, StatusDialog, TenantDialog } from "./CustomerDialogs";
import PharmacyTickets from "../tickets/PharmacyTickets";
import CustomerSubscriptionCard from "../billing/CustomerSubscriptionCard";
import PharmacyAssetsCard from "../procurement/PharmacyAssetsCard";
import { CustomerRecordsCard } from "../records/RelatedRecords";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuSeparator as DropdownMenuSeparator, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../ui/responsive-dropdown-menu";
import {
  Activity, AlertTriangle, ChevronRight, GraduationCap, History, Link2, MapPinned, MoreHorizontal, Plus, Printer, RefreshCw, ShieldCheck, Store, Target,
} from "lucide-react";

const PRIMARY_LABELS = { activate: "Déclarer la mise en service", resume: "Reprendre", reopen: "Rouvrir le dossier" };
const MENU_ACTIONS = ["suspend", "terminate", "renounce"];

function Fact({ label, children, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900 break-words">{children || <span className="text-slate-400">—</span>}</dd>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Icon size={16} className="text-slate-400" aria-hidden="true" />{children}</h3>;
}

function RelatedLine({ to, children, testid }) {
  const body = <><span className="min-w-0 flex-1 text-sm text-slate-700">{children}</span>{to && <ChevronRight size={15} className="text-slate-300 shrink-0" />}</>;
  if (!to) return <div className="flex items-center gap-2 py-2" data-testid={testid}>{body}</div>;
  return <Link to={to} className="flex items-center gap-2 py-2 -mx-2 px-2 rounded-md hover:bg-slate-50" data-testid={testid}>{body}</Link>;
}

function plural(n, one, many) {
  return `${n} ${n > 1 ? many : one}`;
}

// What the field modules have for this pharmacy (D-41): each block exists only if the reader may see it in its own module.
function FieldWork({ related, prospectId, canOpenProspect }) {
  const { pilot, missions, equipment, training } = related || {};
  if (!pilot && !missions && !equipment && !training) return null;
  return (
    <Card className="p-5 space-y-2" data-testid="customer-related">
      <SectionTitle icon={MapPinned}>Sur le terrain</SectionTitle>
      <div className="divide-y divide-slate-100">
        {pilot && (
          <RelatedLine to={pilot.link} testid="customer-related-pilot">
            <ShieldCheck size={14} className="inline mr-1.5 text-slate-400" aria-hidden="true" />
            Pilote {pilot.status === "completed" ? "terminé" : pilot.status === "cancelled" ? "annulé" : "en cours"}
            {pilot.health && <span className={`ml-2 rounded-full border px-1.5 py-px text-2xs ${healthTone(pilot.health)}`}>{HEALTH_LABELS[pilot.health] || pilot.health}</span>}
          </RelatedLine>
        )}
        {missions && (
          <RelatedLine to={canOpenProspect ? `/prospects/${prospectId}?tab=missions` : "/missions"} testid="customer-related-missions">
            <MapPinned size={14} className="inline mr-1.5 text-slate-400" aria-hidden="true" />
            {missions.in_progress.length > 0 ? `${plural(missions.in_progress.length, "mission en cours", "missions en cours")} · ` : ""}
            {missions.next ? `prochaine : ${missions.next.title} le ${formatDay(missions.next.planned_start_at)}` : "aucune mission à venir"}
            {missions.last_completed && ` · dernière terminée : ${missions.last_completed.title}`}
            {missions.reports_to_validate > 0 && ` · ${plural(missions.reports_to_validate, "compte rendu à valider", "comptes rendus à valider")}`}
          </RelatedLine>
        )}
        {equipment && (
          <RelatedLine to={canOpenProspect ? `/prospects/${prospectId}?tab=equipment` : "/equipements"} testid="customer-related-equipment">
            <Printer size={14} className="inline mr-1.5 text-slate-400" aria-hidden="true" />
            {equipment.assets !== null ? `${plural(equipment.assets, "équipement en service", "équipements en service")} · ` : ""}
            {equipment.open_issues === 0 ? "aucune anomalie ouverte"
              : `${plural(equipment.open_issues, "anomalie ouverte", "anomalies ouvertes")}${equipment.by_severity.critical ? ` dont ${equipment.by_severity.critical} critique${equipment.by_severity.critical > 1 ? "s" : ""}` : ""}`}
          </RelatedLine>
        )}
        {training && (
          <RelatedLine to={canOpenProspect ? `/prospects/${prospectId}?tab=training` : "/formations"} testid="customer-related-training">
            <GraduationCap size={14} className="inline mr-1.5 text-slate-400" aria-hidden="true" />
            {training.total === 0 ? "aucune session de formation"
              : `${plural(training.upcoming, "session à venir", "sessions à venir")}${training.to_close ? ` · ${training.to_close} à clôturer` : ""}`}
            {training.last_closed && ` · dernière : ${training.last_closed.program || training.last_closed.reference} le ${formatDay(training.last_closed.date)}`}
          </RelatedLine>
        )}
      </div>
    </Card>
  );
}

const FACT_DOTS = { at_risk: "bg-red-500", attention: "bg-amber-500", unknown: "bg-slate-300", on_track: "bg-emerald-500" };

// Health (07-3, D-15 to D-18): the state, the facts that explain it (worst first), and the manual correction with its author.
function HealthCard({ account, onCorrect }) {
  const h = account.health;
  if (!h || !h.state) return null;
  return (
    <Card className="p-5 space-y-3" data-testid="customer-health-card">
      <div className="flex items-center justify-between gap-2">
        <SectionTitle icon={Activity}>Santé du dossier</SectionTitle>
        {account.capabilities?.override_health && (
          <Button variant="outline" size="sm" onClick={onCorrect} data-testid="customer-action-health">Corriger la santé</Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <HealthBadge state={h.state} overridden={!!h.override} />
        {h.override && (
          <span className="text-xs text-slate-500" data-testid="customer-health-computed">Santé calculée : {CUSTOMER_HEALTH_LABELS[h.computed] || "—"}</span>
        )}
      </div>
      {h.override && (
        <p className="text-sm text-slate-700 rounded-md bg-slate-50 px-3 py-2" data-testid="customer-health-override">
          Correction manuelle de {h.override.by} le {formatDay(h.override.at)} : « {h.override.reason} »
        </p>
      )}
      <ul className="space-y-1.5" data-testid="customer-health-facts">
        {h.facts.map((f) => (
          <li key={f.code} className="flex items-start gap-2 text-sm text-slate-700">
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${FACT_DOTS[f.level] || FACT_DOTS.unknown}`} aria-hidden="true" />
            <span>{f.text}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const NEXT_STATES = { in_progress: ["reached", "dropped"], reached: ["in_progress"], dropped: ["in_progress"] };
const STATE_ACTION_LABELS = { reached: "Marquer atteint", dropped: "Abandonner", in_progress: "Remettre en cours" };
const OBJECTIVE_TONES = { in_progress: "bg-sky-50 text-sky-800 border-sky-200", reached: "bg-emerald-50 text-emerald-800 border-emerald-200",
  dropped: "bg-slate-100 text-slate-600 border-slate-200" };

function ObjectivesCard({ account, onAdd, onEdit, onChanged }) {
  const { user } = useAuth();
  const canEdit = !!account.capabilities?.objectives;
  const canDelete = canEdit && hasPermission(user, "customers:delete");
  const [deleting, setDeleting] = useState(null);
  const objectives = account.objectives || [];
  const confirmDelete = async (reason) => {
    const target = deleting;
    try {
      await api.delete(`/customers/${account.prospect_id}/objectives/${target.id}`, { data: { reason } });
      toast.success("Objectif mis à la corbeille");
      onChanged();
    } catch (e) {
      toast.error(customerErrorMessage(e, "Impossible de supprimer l'objectif."));
    } finally {
      setDeleting(null);
    }
  };
  if (!canEdit && objectives.length === 0) return null;
  const move = async (objective, state) => {
    try {
      await api.patch(`/customers/${account.prospect_id}/objectives/${objective.id}`, { expected_version: objective.version, state });
      toast.success(`Objectif ${OBJECTIVE_STATE_LABELS[state].toLowerCase()}`);
      onChanged();
    } catch (e) {
      toast.error(customerErrorMessage(e, "Impossible de modifier l'objectif."));
      onChanged();
    }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="customer-objectives">
      <div className="flex items-center justify-between gap-2">
        <SectionTitle icon={Target}>Objectifs du suivi</SectionTitle>
        {canEdit && <Button variant="outline" size="sm" onClick={onAdd} data-testid="customer-objective-add"><Plus size={14} className="mr-1" />Ajouter</Button>}
      </div>
      {objectives.length === 0 ? (
        <p className="text-sm text-slate-500">Aucun objectif. Fixez ce que le suivi de cette pharmacie doit obtenir.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {objectives.map((o) => (
            <li key={o.id} className="flex items-start gap-3 py-2" data-testid={`customer-objective-${o.id}`}>
              <div className="min-w-0 flex-1">
                <p className={`text-sm break-words ${o.state === "dropped" ? "text-slate-500 line-through" : "text-slate-900"}`}>{o.label}</p>
                <p className="text-xs text-slate-500">
                  {o.due_date ? `Échéance ${formatDay(o.due_date)}` : "Sans échéance"}{o.updated_by ? ` · ${o.updated_by}` : ""}
                </p>
              </div>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${OBJECTIVE_TONES[o.state]}`} data-testid="customer-objective-state">
                {OBJECTIVE_STATE_LABELS[o.state]}
              </span>
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" aria-label={`Actions sur l'objectif ${o.label}`}
                      data-testid={`customer-objective-menu-${o.id}`}><MoreHorizontal size={15} /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" title="Objectif">
                    {NEXT_STATES[o.state].map((state) => (
                      <DropdownMenuItem key={state} onClick={() => move(o, state)} data-testid={`customer-objective-to-${state}`}>
                        {STATE_ACTION_LABELS[state]}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem onClick={() => onEdit(o)} data-testid="customer-objective-edit">Modifier</DropdownMenuItem>
                    {canDelete && <DropdownMenuItem onClick={() => setDeleting(o)} className="text-red-600 focus:text-red-700" data-testid="customer-objective-delete">Supprimer</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </li>
          ))}
        </ul>
      )}
      <DeleteWithReasonDialog
        target={deleting} testId="delete-objective" onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title="Supprimer cet objectif ?" description="Il va dans la corbeille : vous pourrez le restaurer pendant 30 jours."
      />
    </Card>
  );
}

// The customer account of one pharmacy: on the prospect's « Client » tab and on /clients/:prospectId (for a reader who cannot
// open the prospect). Every action is offered from the server's capabilities and checked again by the server.
export default function CustomerPanel({ prospectId, onLoaded }) {
  const [state, setState] = useState({ phase: "loading" });
  const [dialog, setDialog] = useState(null);  // {kind, action}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${prospectId}`);
      setState({ phase: "ready", account: data });
      onLoaded?.(data);
    } catch (e) {
      if (e?.response?.status === 404) setState({ phase: "missing" });
      else setState({ phase: "error", message: customerErrorMessage(e, "Impossible de charger le dossier client.") });
    }
  }, [prospectId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const done = (message) => () => {
    setDialog(null);
    toast.success(message);
    load();
  };

  if (state.phase === "loading") {
    return <div className="space-y-3" data-testid="customer-loading"><Skeleton className="h-28 w-full" /><Skeleton className="h-40 w-full" /></div>;
  }
  if (state.phase === "missing") {
    return (
      <Card className="p-8 text-center space-y-2" data-testid="customer-missing">
        <Store className="mx-auto text-slate-300" size={26} />
        <h3 className="font-medium text-slate-900">Pas de dossier client</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">Le dossier s'ouvre tout seul quand la pharmacie passe à « Gagné ». S'il existe, il ne fait pas partie des dossiers que vous suivez.</p>
      </Card>
    );
  }
  if (state.phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="customer-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{state.message}</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }

  const a = state.account;
  const caps = a.capabilities || {};
  const primary = primaryAction(caps);
  const menu = MENU_ACTIONS.filter((id) => caps[id]);
  const hasMenu = caps.edit || menu.length > 0 || caps.correct_activation || caps.link_tenant;
  const openPrimary = () => setDialog(primary === "activate" ? { kind: "activation" } : { kind: "status", action: primary });

  return (
    <div className="space-y-4" data-testid="customer-panel">
      {/* Full width; from a laptop up, the follow-up (health, field) sits in a side column beside the account and its history. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_26rem] items-start">
        <div className="min-w-0 space-y-4">
          <Card className="relative p-5 space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              {/* On a phone the « … » menu sits in the card's corner (pr-14 = the button and its margin keeps the axes clear of it), never on a line of its own; a badge longer than its column (« Avant mise en service ») wraps instead of running under it. */}
              <div className="grid grid-cols-2 gap-4 pr-14 sm:flex sm:gap-8 md:pr-0 [&>div]:min-w-0 [&_span]:whitespace-normal sm:[&_span]:whitespace-nowrap" data-testid="customer-axes">
                <div className="space-y-1"><p className="text-xs text-slate-500">Mise en service</p><PhaseBadge phase={a.phase} /></div>
                <div className="space-y-1"><p className="text-xs text-slate-500">Statut</p><StatusBadge status={a.status} closeCode={a.close_code} /></div>
                <div className="space-y-1"><p className="text-xs text-slate-500">Santé</p>
                  <HealthBadge state={a.health?.state} overridden={!!a.health?.override} testid="customer-health-axis" /></div>
              </div>
              <div className="flex items-center gap-2">
                {primary && (
                  <Button onClick={openPrimary} className="flex-1 md:flex-none" data-testid={`customer-action-${primary}`}>{PRIMARY_LABELS[primary]}</Button>
                )}
                {hasMenu && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="absolute right-4 top-4 md:static" aria-label="Autres actions du dossier"
                        data-testid="customer-actions-menu"><MoreHorizontal size={16} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" title="Dossier client">
                      {caps.edit && <DropdownMenuItem onClick={() => setDialog({ kind: "edit" })} data-testid="customer-action-edit">Modifier</DropdownMenuItem>}
                      {menu.map((id) => (
                        <DropdownMenuItem key={id} onClick={() => setDialog({ kind: "status", action: id })} data-testid={`customer-action-${id}`}
                          className={STATUS_ACTIONS[id].danger ? "text-red-700" : ""}>
                          {STATUS_ACTIONS[id].button}
                        </DropdownMenuItem>
                      ))}
                      {(caps.correct_activation || caps.link_tenant) && (caps.edit || menu.length > 0) && <DropdownMenuSeparator />}
                      {caps.correct_activation && <DropdownMenuItem onClick={() => setDialog({ kind: "correction" })} data-testid="customer-action-correct">Corriger la date de mise en service</DropdownMenuItem>}
                      {caps.link_tenant && (
                        <DropdownMenuItem onClick={() => setDialog({ kind: "tenant" })} data-testid="customer-action-tenant">
                          {a.tenant_link ? "Compte Jamora (vérifier, délier)" : "Lier le compte Jamora"}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
              <Fact label="Responsable du compte" testid="customer-manager">
                {a.account_manager ? `${a.account_manager.name}${a.account_manager.active ? "" : " (compte désactivé)"}` : null}
              </Fact>
              <Fact label="Commercial">{a.commercial?.name}</Fact>
              <Fact label="Gain commercial">{a.commercial_won_at ? formatDay(a.commercial_won_at) : "Date inconnue"}</Fact>
              <Fact label="Signature du contrat" testid="customer-signed">{formatDay(a.contract_signed_at)}</Fact>
              <Fact label="Mise en service" testid="customer-activated">{formatDay(a.service_activated_at)}</Fact>
              <Fact label="Prochaine revue" testid="customer-review">{formatDay(a.next_review_date)}</Fact>
              {a.terminated_at && <Fact label="Fin">{formatDay(a.terminated_at)}</Fact>}
              <Fact label="Compte Jamora" testid="customer-tenant">
                {a.tenant_link
                  ? <span className="inline-flex items-center gap-1"><Link2 size={13} className="text-slate-400" aria-hidden="true" />
                    {a.tenant_link.tenant_ref ? <span className="font-mono">{a.tenant_link.tenant_ref}</span> : "Déclaré"}
                    <span className="text-slate-500">{a.tenant_link.state === "verified" ? "— vérifié" : "— non vérifié"}</span></span>
                  : "Non lié"}
              </Fact>
            </dl>
          </Card>
          <ObjectivesCard account={a} onAdd={() => setDialog({ kind: "objective" })} onEdit={(o) => setDialog({ kind: "objective", objective: o })} onChanged={load} />
          <Card className="p-5 space-y-3" data-testid="customer-history">
            <SectionTitle icon={History}>Historique du dossier</SectionTitle>
            {a.history.length === 0 ? <p className="text-sm text-slate-500">Aucun événement.</p> : (
              <ol className="space-y-3">
                {a.history.map((h) => (
                  <li key={h.id} className="text-sm">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium text-slate-900">{historyLabel(h)}</span>
                      <span className="text-xs text-slate-500">
                        {formatDay(h.at)}{h.effective_date && h.effective_date !== String(h.at).slice(0, 10) ? ` (effet le ${formatDay(h.effective_date)})` : ""}
                        {" · "}{h.actor || (h.source === "backfill" ? "Reprise automatique" : "Automatique")}
                      </span>
                    </div>
                    {h.reason && <p className="text-slate-600 break-words">« {h.reason} »</p>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
        <div className="min-w-0 space-y-4">
          <HealthCard account={a} onCorrect={() => setDialog({ kind: "health" })} />
          <CustomerSubscriptionCard prospectId={a.prospect_id} />
          <CustomerRecordsCard account={a} onChanged={load} />
          <PharmacyAssetsCard prospectId={a.prospect_id} />
          <FieldWork related={a.related} prospectId={a.prospect_id} canOpenProspect={a.can_open_prospect} />
          <PharmacyTickets prospectId={a.prospect_id} pharmacyName={a.pharmacy?.name} compact />
        </div>
      </div>

      <ActivationDialog account={a} open={dialog?.kind === "activation"} onOpenChange={(o) => !o && setDialog(null)} onDone={done("Mise en service déclarée")} />
      <ActivationDialog account={a} correction open={dialog?.kind === "correction"} onOpenChange={(o) => !o && setDialog(null)} onDone={done("Date de mise en service corrigée")} />
      <StatusDialog account={a} action={dialog?.kind === "status" ? dialog.action : null} open={dialog?.kind === "status"}
        onOpenChange={(o) => !o && setDialog(null)} onDone={done("Dossier client mis à jour")} />
      <EditAccountDialog account={a} open={dialog?.kind === "edit"} onOpenChange={(o) => !o && setDialog(null)} onDone={done("Modifications enregistrées")} />
      <HealthDialog account={a} open={dialog?.kind === "health"} onOpenChange={(o) => !o && setDialog(null)} onDone={done("Santé du dossier enregistrée")} />
      <ObjectiveDialog account={a} objective={dialog?.kind === "objective" ? dialog.objective : null} open={dialog?.kind === "objective"}
        onOpenChange={(o) => !o && setDialog(null)} onDone={done("Objectif enregistré")} />
      <TenantDialog account={a} open={dialog?.kind === "tenant"} onOpenChange={(o) => !o && setDialog(null)} onDone={done("Liaison Jamora mise à jour")} />
    </div>
  );
}
